import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Thermometer, ShieldCheck, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function QrLanding() {
  const { token } = useParams();
  const { user, loading, login } = useAuth();
  const [resolved, setResolved] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(true);

  // login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  // temp entry
  const [temp, setTemp] = useState("");
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/qr/resolve/${token}`)
      .then((r) => setResolved(r.data))
      .catch((e) => setError(e?.normalized?.message || "This code is invalid or is no longer active"))
      .finally(() => setBusy(false));
  }, [token]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try { await login(email, password); }
    catch (err) { toast.error(err?.normalized?.message || "Sign in failed"); }
    finally { setLoggingIn(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (temp === "") { toast.error("Enter a temperature"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/qr/submit/temperature", { token, temperature: parseFloat(temp), comments });
      setDone(data.record);
      toast.success("Temperature recorded");
    } catch (err) {
      const n = err?.normalized;
      if (n?.status === 400) toast.error(n.message);
      else if (n?.status === 403) toast.error("You don't have access to this site or workflow");
      else if (n?.status === 404) toast.error("This code is invalid or is no longer active");
      else toast.error(n?.message || "Could not record");
    } finally { setSubmitting(false); }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-5" data-testid="qr-landing">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-5"><Logo /></div>
        {children}
      </div>
    </div>
  );

  if (busy || loading) return <Shell><div className="cc-card p-10 flex justify-center"><Loader2 className="animate-spin text-brand" /></div></Shell>;

  if (error) return (
    <Shell>
      <div className="cc-card p-8 text-center" data-testid="qr-landing-error">
        <AlertTriangle className="mx-auto text-amber-500" size={28} />
        <h1 className="font-display text-lg font-bold text-slate-800 mt-2">Code unavailable</h1>
        <p className="text-sm text-slate-500 mt-1">{error}</p>
      </div>
    </Shell>
  );

  const header = (
    <div className="text-center mb-4">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brand/10 text-brand mb-2"><Thermometer size={24} /></div>
      <h1 className="font-display text-xl font-bold text-slate-800" data-testid="qr-landing-action">{resolved.action_label}</h1>
      <p className="text-sm text-slate-500">{resolved.target_label}{resolved.site_name ? ` · ${resolved.site_name}` : ""}</p>
    </div>
  );

  if (done) return (
    <Shell>
      <div className="cc-card p-8 text-center" data-testid="qr-landing-success">
        <CheckCircle2 className="mx-auto text-emerald-500" size={30} />
        <h1 className="font-display text-lg font-bold text-slate-800 mt-2">Recorded</h1>
        <p className="text-sm text-slate-500 mt-1">{done.temperature}{done.unit} logged{done.passed ? "" : " — flagged as an exception"}.</p>
        <button onClick={() => { setDone(null); setTemp(""); setComments(""); }} data-testid="qr-landing-again"
          className="mt-4 w-full px-4 py-2.5 rounded-xl bg-brand text-white font-semibold text-sm">Record another</button>
      </div>
    </Shell>
  );

  if (!user) return (
    <Shell>
      {header}
      <form onSubmit={doLogin} className="cc-card p-6 space-y-3" data-testid="qr-landing-login">
        <div className="flex items-center gap-1.5 text-xs text-slate-500"><ShieldCheck size={14} className="text-brand" /> Sign in to record this reading</div>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="qr-landing-email"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="qr-landing-password"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
        <button type="submit" disabled={loggingIn} data-testid="qr-landing-login-submit"
          className="w-full px-4 py-2.5 rounded-xl bg-brand text-white font-semibold text-sm disabled:opacity-40">{loggingIn ? "Signing in…" : "Sign in"}</button>
      </form>
    </Shell>
  );

  return (
    <Shell>
      {header}
      <form onSubmit={submit} className="cc-card p-6 space-y-3" data-testid="qr-landing-form">
        <div>
          <label className="text-xs font-semibold text-slate-600">Temperature (°C)</label>
          <input type="number" step="0.1" required value={temp} onChange={(e) => setTemp(e.target.value)} data-testid="qr-landing-temp"
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" autoFocus />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Comment (required if out of range)</label>
          <input value={comments} onChange={(e) => setComments(e.target.value)} data-testid="qr-landing-comment"
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
        </div>
        <button type="submit" disabled={submitting} data-testid="qr-landing-submit"
          className="w-full px-4 py-2.5 rounded-xl bg-brand text-white font-semibold text-sm disabled:opacity-40">{submitting ? "Saving…" : "Record temperature"}</button>
        <p className="text-[11px] text-slate-400 text-center">Signed in as {user.name}. Your normal permissions apply.</p>
      </form>
    </Shell>
  );
}
